import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// import supabase from '../utils/supabaseClient';
import proxyClient, { getAccessToken } from '../utils/proxyClient';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Stack,
  useMediaQuery,
  useTheme,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip
} from '@mui/material';
import { Google as GoogleIcon, LinkedIn as LinkedInIcon, Apple as AppleIcon, Microsoft as MicrosoftIcon } from '@mui/icons-material';
import { useAuth } from '../App';
import logoImage from '../assets/logo.svg';
import backgroundImage from '../assets/lp.jpg';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signUpOpen, setSignUpOpen] = useState(false);
  const [signUpEmail, setSignUpEmail] = useState('');
  const [signUpPassword, setSignUpPassword] = useState('');
  const [signUpConfirmPassword, setSignUpConfirmPassword] = useState('');
  const [signUpSuccess, setSignUpSuccess] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    console.log('Login page: User state:', user ? 'authenticated' : 'not authenticated');
    
    // Check if we're redirected from successful authentication
    const params = new URLSearchParams(window.location.search);
    if (params.get('authenticated') === 'true') {
      console.log('Login page: Authentication redirect detected, not clearing state');
      return;
    }
    
    // Only clear auth when specifically on login page without being redirected
    if (window.location.pathname === '/login' && !window.location.search.includes('authenticated')) {
      console.log('Login page: On login page directly, state will be managed by login form');
    }
    
    if (user) {
      console.log('Login page: Redirecting to dashboard because user is already logged in');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    // Set flag that login is in progress to prevent session clearing
    localStorage.setItem('isLoginInProgress', 'true');
    console.log('Login form submitted, attempting to authenticate...');

    try {
      console.log('Sending request to backend login endpoint...');
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const sessionData = await response.json();
      console.log('Login response received, status:', response.status, response.ok ? 'OK' : 'Failed');

      if (!response.ok) {
        console.error('Login failed:', sessionData);
        throw new Error(sessionData.message || 'Failed to sign in');
      }

      console.log('Authentication successful, setting session...');
      if (sessionData.access_token && sessionData.refresh_token && sessionData.user) {
        const { error: sessionError } = await proxyClient.auth.setSession({
          access_token: sessionData.access_token,
          refresh_token: sessionData.refresh_token,
        });
        
        if (sessionError) {
          console.error('Error setting session:', sessionError);
          throw new Error('Authentication successful but failed to establish session');
        }
        
        console.log('Session set, fetching user profile...');
      // Login successful, clear the login in progress flag
      localStorage.removeItem('isLoginInProgress');
        // Use the backend API to check user role
        const token = sessionData.access_token;
        const profileResponse = await fetch('/api/auth/profile', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!profileResponse.ok) {
          console.error('Error fetching user profile after login:', profileResponse.statusText);
          throw new Error('Authentication successful but failed to fetch user profile');
        }

        const profile = await profileResponse.json();

        // Set a flag in sessionStorage to prevent auth loop
        sessionStorage.setItem('authNavigationInProgress', 'true');

        // Always navigate to dashboard regardless of user role
        console.log('User logged in, navigating to dashboard');
        navigate('/dashboard?authenticated=true');
        
        /* Original code commented out
        if (profile?.role === 'admin') {
          console.log('Admin user, navigating to admin panel');
          navigate('/admin?authenticated=true');
        } else {
          console.log('Regular user, navigating to dashboard');
          navigate('/dashboard?authenticated=true');
        }
        */
      } else {
        console.error('Login response missing expected data:', sessionData);
        throw new Error(sessionData.message || 'Login successful, but no complete session data received.');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      setError(error.message || 'An error occurred during sign in');
      // Login failed, clear the login in progress flag
      localStorage.removeItem('isLoginInProgress');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (signUpPassword !== signUpConfirmPassword) {
      setError("Passwords don't match");
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: signUpEmail, password: signUpPassword }),
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.message || 'Failed to sign up');
      }

      if (responseData.user) {
        if (responseData.access_token && responseData.refresh_token) {
            await proxyClient.auth.setSession({
                access_token: responseData.access_token,
                refresh_token: responseData.refresh_token,
            });
        }
        
        // Create user profile using backend API
        const token = responseData.access_token;
        try {
          const profileResponse = await fetch('/api/auth/profiles', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              id: responseData.user.id,
              email: signUpEmail,
              role: 'pending',
              status: 'pending_approval'
            })
          });
          
          if (!profileResponse.ok) {
            const profileError = await profileResponse.json();
            console.error("Error creating profile after sign up:", profileError);
            setError(`Signup successful, but profile creation failed. Please contact support.`);
          }
        } catch (profileError) {
          console.error("Error creating profile after sign up:", profileError);
          setError(`Signup successful, but profile creation failed. Please contact support.`);
        }

        setSignUpSuccess(true);
        setTimeout(() => {
          setSignUpOpen(false);
          setSignUpSuccess(false);
          setSignUpEmail('');
          setSignUpPassword('');
          setSignUpConfirmPassword('');
          setError(null);
        }, 3000);

      } else {
        throw new Error(responseData.message || 'Sign up did not return user information.');
      }
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get the backend URL from env or use default
      const backendUrl = process.env.REACT_APP_PROXY_URL || 'http://localhost:5000';
      
      // Check backend health first
      try {
        const healthCheck = await fetch(`${backendUrl}/api/auth/health`, { 
          method: 'GET',
        });
        
        if (!healthCheck.ok) {
          throw new Error('Authentication service is not available');
        }
        
        const healthData = await healthCheck.json();
        console.log('Backend health check:', healthData);
      } catch (error) {
        console.error('Backend health check failed:', error);
        setError('Could not connect to authentication service. Please use email/password login instead.');
        setLoading(false);
        return;
      }
      
      // Show a message to the user
      setError('Social login is not fully configured in this demo. Please use email/password login instead.');
      setLoading(false);
      
      // Uncomment this to enable the actual OAuth redirect when it's properly configured
      /*
      console.log(`Redirecting to OAuth: ${backendUrl}/api/auth/oauth/start/google`);
      window.location.href = `${backendUrl}/api/auth/oauth/start/google?redirectUrl=${encodeURIComponent(window.location.origin + '/dashboard')}`;
      */
    } catch (error) {
      console.error('Error initiating Google sign-in:', error);
      setError('Failed to start authentication. Please use email/password login instead.');
      setLoading(false);
    }
  };

  const handleLinkedInSignIn = async () => {
    try {
      setLoading(true);
      setError(null);
      setError('Social login is not fully configured in this demo. Please use email/password login instead.');
      setLoading(false);
    } catch (error) {
      console.error('Error initiating LinkedIn sign-in:', error);
      setError('Failed to start authentication. Please use email/password login instead.');
      setLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    try {
      setLoading(true);
      setError(null);
      setError('Social login is not fully configured in this demo. Please use email/password login instead.');
      setLoading(false);
    } catch (error) {
      console.error('Error initiating Apple sign-in:', error);
      setError('Failed to start authentication. Please use email/password login instead.');
      setLoading(false);
    }
  };

  const handleMicrosoftSignIn = async () => {
    try {
      setLoading(true);
      setError(null);
      setError('Social login is not fully configured in this demo. Please use email/password login instead.');
      setLoading(false);
    } catch (error) {
      console.error('Error initiating Microsoft sign-in:', error);
      setError('Failed to start authentication. Please use email/password login instead.');
      setLoading(false);
    }
  };

  // Add this useEffect to handle OAuth redirects and token exchange
  useEffect(() => {
    // Extract tokens from URL if they exist (after OAuth redirect)
    const params = new URLSearchParams(window.location.search);
    const access_token = params.get('access_token');
    const refresh_token = params.get('refresh_token');
    const error = params.get('error');
    const error_description = params.get('error_description');
    
    // Handle OAuth errors
    if (error) {
      console.error('OAuth error:', error, error_description);
      setError(`Login failed: ${error_description || error}`);
      // Clean URL
      window.history.replaceState(null, '', '/login');
      return;
    }
    
    // Handle successful OAuth login
    if (access_token && refresh_token) {
      console.log('Login page: OAuth tokens found in URL, processing...');
      setLoading(true);
      
      (async () => {
        try {
          // Exchange the tokens for session
          const { data, error: sessionError } = await proxyClient.auth.setSession({
            access_token,
            refresh_token
          });
          
          if (sessionError) {
            console.error('Error setting session from OAuth tokens:', sessionError);
            setError('Authentication successful but failed to establish session');
            setLoading(false);
            return;
          }
          
          if (data?.user) {
            console.log('Login with OAuth successful, checking user profile...');
            
            // Get user profile to determine redirect
            try {
              const token = getAccessToken();
              const profileResponse = await fetch('/api/auth/profile', {
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              });
    
              if (!profileResponse.ok) {
                console.error('Error fetching profile after OAuth login:', profileResponse.statusText);
                throw new Error('Authentication successful but failed to fetch user profile');
              }
    
              const profile = await profileResponse.json();
              
              // Set flag to prevent auth loop
              sessionStorage.setItem('authNavigationInProgress', 'true');
    
              // Redirect based on role
              if (profile?.role === 'admin') {
                console.log('Admin user authenticated with OAuth, navigating to admin panel');
                navigate('/admin?authenticated=true');
              } else {
                console.log('User authenticated with OAuth, navigating to dashboard');
                navigate('/dashboard?authenticated=true');
              }
            } catch (profileError: any) {
              console.error('Error processing profile after OAuth login:', profileError);
              setError(profileError.message || 'Error processing login');
              setLoading(false);
            }
          }
        } catch (error: any) {
          console.error('Error processing OAuth login:', error);
          setError(error.message || 'Authentication failed');
          setLoading(false);
        }
      })();
      
      // Clean URL regardless of outcome
      window.history.replaceState(null, '', '/login');
    }
  }, [navigate]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        py: 3,
      }}
    >
      <Container maxWidth="xs" sx={{ position: 'relative', zIndex: 1 }}>
        <Paper
          elevation={3}
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            background: 'rgba(255, 255, 255, 0.6)',
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.7)',
              transform: 'translateY(-2px)'
            }
          }}
        >
          <Stack 
            direction="row" 
            alignItems="center" 
            spacing={1}
            sx={{ 
              mb: 3,
              justifyContent: 'center',
              maxWidth: '100%',
              mx: 'auto'
            }}
          >
            <img 
              src={logoImage} 
              alt="Logo" 
              style={{ 
                width: isMobile ? 80 : 100,
                height: isMobile ? 80 : 100,
                filter: 'drop-shadow(0 2px 3px rgba(0, 0, 0, 0.2))'
              }} 
            />
          </Stack>

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 2, 
                width: '100%',
                backgroundColor: 'rgba(253, 237, 237, 0.95)'
              }}
            >
              {error}
            </Alert>
          )}

          <Stack 
            direction="row" 
            spacing={2} 
            sx={{ 
              width: '100%', 
              mb: 2, 
              justifyContent: 'center' 
            }}
          >
            <Tooltip title="Continue with Google">
              <IconButton
                onClick={handleGoogleSignIn}
                sx={{
                  backgroundColor: '#fff',
                  color: '#757575',
                  width: 50,
                  height: 50,
                  '&:hover': {
                    backgroundColor: '#f1f1f1',
                  },
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}
              >
                <GoogleIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Continue with LinkedIn">
              <IconButton
                onClick={handleLinkedInSignIn}
                sx={{
                  backgroundColor: '#0077b5',
                  color: '#fff',
                  width: 50,
                  height: 50,
                  '&:hover': {
                    backgroundColor: '#006097',
                  },
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}
              >
                <LinkedInIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Continue with Apple">
              <IconButton
                onClick={handleAppleSignIn}
                sx={{
                  backgroundColor: '#000',
                  color: '#fff',
                  width: 50,
                  height: 50,
                  '&:hover': {
                    backgroundColor: '#333',
                  },
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}
              >
                <AppleIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Continue with Microsoft">
              <IconButton
                onClick={handleMicrosoftSignIn}
                sx={{
                  backgroundColor: '#05a6f0',
                  color: '#fff',
                  width: 50,
                  height: 50,
                  '&:hover': {
                    backgroundColor: '#0590d0',
                  },
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}
              >
                <MicrosoftIcon />
              </IconButton>
            </Tooltip>
          </Stack>

          <Divider sx={{ width: '100%', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              or
            </Typography>
          </Divider>

          <form onSubmit={handleSubmit} style={{ width: '100%', marginTop: '20px' }}>
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                sx={{
                  '& .MuiInputBase-root': {
                    background: 'rgba(255, 255, 255, 0.7)'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(0, 0, 0, 0.7)',
                    '&.Mui-focused': {
                      color: 'primary.main'
                    }
                  }
                }}
                InputLabelProps={{
                  shrink: true,
                  sx: { 
                    background: 'transparent',
                    px: 0.5
                  }
                }}
              />
            </Box>
            
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                sx={{
                  '& .MuiInputBase-root': {
                    background: 'rgba(255, 255, 255, 0.7)'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(0, 0, 0, 0.7)',
                    '&.Mui-focused': {
                      color: 'primary.main'
                    }
                  }
                }}
                InputLabelProps={{
                  shrink: true,
                  sx: { 
                    background: 'transparent',
                    px: 0.5
                  }
                }}
              />
            </Box>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{ 
                mt: 2,
                bgcolor: 'rgba(0, 0, 0, 0.85)',
                color: '#FFFFFF',
                padding: '8px 0',
                fontSize: '0.9rem',
                fontWeight: 500,
                textTransform: 'none',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.95)',
                  transform: 'translateY(-1px)'
                }
              }}
            >
              {loading ? <CircularProgress size={24} /> : 'Sign In'}
            </Button>

            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.7)' }}>
                Don't have an account?{' '}
                <Button
                  onClick={() => setSignUpOpen(true)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    color: 'rgba(0, 0, 0, 0.85)',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      textDecoration: 'underline'
                    }
                  }}
                >
                  Sign Up
                </Button>
              </Typography>
            </Box>
          </form>

          {/* Social Login Buttons */}
          <Box sx={{ mt: 3, mb: 2 }}>
            <Divider>
              <Typography variant="body2" color="textSecondary" sx={{ px: 1 }}>
                Or continue with
              </Typography>
            </Divider>
            
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, flexWrap: 'wrap', gap: 2 }}>
              <Tooltip title="Sign in with Google">
                <IconButton
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                  sx={{ 
                    border: '1px solid', 
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1,
                    opacity: 0.5,
                  }}
                  aria-label="Sign in with Google"
                >
                  <GoogleIcon />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Sign in with LinkedIn">
                <IconButton
                  onClick={handleLinkedInSignIn}
                  disabled={loading}
                  sx={{ 
                    border: '1px solid', 
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1,
                    opacity: 0.5,
                  }}
                  aria-label="Sign in with LinkedIn"
                >
                  <LinkedInIcon />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Sign in with Microsoft">
                <IconButton
                  onClick={handleMicrosoftSignIn}
                  disabled={loading}
                  sx={{ 
                    border: '1px solid', 
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1,
                    opacity: 0.5,
                  }}
                  aria-label="Sign in with Microsoft"
                >
                  <MicrosoftIcon />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Sign in with Apple">
                <IconButton
                  onClick={handleAppleSignIn}
                  disabled={loading}
                  sx={{ 
                    border: '1px solid', 
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1,
                    opacity: 0.5,
                  }}
                  aria-label="Sign in with Apple"
                >
                  <AppleIcon />
                </IconButton>
              </Tooltip>
            </Box>

            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
              Note: Social login requires configuration in Supabase dashboard.
              Please use email/password login.
            </Typography>
          </Box>
        </Paper>
      </Container>

      {/* Sign Up Dialog */}
      <Dialog 
        open={signUpOpen} 
        onClose={() => !loading && setSignUpOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            Sign Up
          </Typography>
        </DialogTitle>
        <DialogContent>
          {signUpSuccess ? (
            <Alert severity="success" sx={{ mt: 2 }}>
              Sign up successful! Please wait for admin approval.
            </Alert>
          ) : (
            <Box component="form" onSubmit={handleSignUp} sx={{ mt: 2 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                label="Email"
                value={signUpEmail}
                onChange={(e) => setSignUpEmail(e.target.value)}
                type="email"
                size="small"
              />
              <TextField
                margin="normal"
                required
                fullWidth
                label="Password"
                type="password"
                value={signUpPassword}
                onChange={(e) => setSignUpPassword(e.target.value)}
                size="small"
              />
              <TextField
                margin="normal"
                required
                fullWidth
                label="Confirm Password"
                type="password"
                value={signUpConfirmPassword}
                onChange={(e) => setSignUpConfirmPassword(e.target.value)}
                size="small"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setSignUpOpen(false)}
            disabled={loading}
            sx={{ color: 'rgba(0, 0, 0, 0.7)' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSignUp}
            disabled={loading || signUpSuccess}
            variant="contained"
            sx={{
              bgcolor: 'rgba(0, 0, 0, 0.85)',
              color: '#FFFFFF',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.95)',
              }
            }}
          >
            {loading ? <CircularProgress size={24} /> : 'Sign Up'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Login;
